import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { WhatsAppConversationResponse, WhatsAppMessageResponse } from '@orion/contracts';
import { Button, EmptyState } from '@orion/ui';
import { ApiError } from '../../lib/api-client.js';
import { useToast } from '../../lib/toast.js';
import { PageHeader } from '../../app/shell.js';
import { useAssignableMembersQuery } from '../me/api.js';
import { useAssignWhatsAppConversation, useSendWhatsAppMessage, useWhatsAppConversationQuery, useWhatsAppConversationsQuery } from './api.js';

type Tab = 'open' | 'unassigned' | 'mine' | 'closed';

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'open', label: 'Open' },
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'mine', label: 'Assigned to me' },
  { key: 'closed', label: 'Closed' },
];

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * The WhatsApp Inbox — 3-pane layout per spec §7: conversation list, the
 * chat itself, and a CRM info panel. Text-only composer for v1
 * (`sendWhatsAppMessageInputSchema`); other message types are received
 * and shown (see `MessageBubble`) but not yet composable from here.
 */
export function WhatsAppInboxPage() {
  const [tab, setTab] = useState<Tab>('open');
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const query =
    tab === 'unassigned' ? { unassigned: true, pageSize: 100 } : tab === 'mine' ? { assignedToMe: true, pageSize: 100 } : tab === 'closed' ? { status: 'closed' as const, pageSize: 100 } : { status: 'open' as const, pageSize: 100 };
  const { data, isLoading } = useWhatsAppConversationsQuery(query);
  const conversations = data?.data ?? [];

  return (
    <>
      <PageHeader breadcrumb={[{ label: 'WhatsApp Inbox' }]} />
      <main className="grid flex-1 grid-cols-[300px_1fr_280px] gap-0 overflow-hidden">
        <aside className="flex min-h-0 flex-col border-r border-border bg-surface">
          <div role="tablist" aria-label="WhatsApp inbox views" className="flex h-11 shrink-0 items-stretch border-b border-border px-2">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => setTab(t.key)}
                className={`flex-1 truncate text-[12px] font-semibold ${tab === t.key ? 'text-primary shadow-[inset_0_-2px_0_var(--color-primary)]' : 'text-text-muted hover:text-ink'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {!isLoading && conversations.length === 0 && (
              <div className="p-4">
                <EmptyState title="No conversations" description="Nothing in this view." />
              </div>
            )}
            {conversations.map((c) => (
              <ConversationRow key={c.id} conversation={c} selected={c.id === selectedId} onClick={() => setSelectedId(c.id)} />
            ))}
          </div>
        </aside>

        <ChatPane conversationId={selectedId} />

        <InfoPane conversationId={selectedId} />
      </main>
    </>
  );
}

function ConversationRow({ conversation, selected, onClick }: { conversation: WhatsAppConversationResponse; selected: boolean; onClick: () => void }) {
  const name = conversation.profileName ?? conversation.linkedRecord?.label ?? conversation.phoneE164;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-start gap-2.5 border-b border-divider px-3 py-2.5 text-left ${selected ? 'bg-primary-soft/40' : 'hover:bg-surface-subtle'}`}
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-night text-[11px] font-[650] text-white">{initials(name)}</span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-center justify-between gap-2">
          <span className="truncate text-[13px] font-semibold text-ink">{name}</span>
          {conversation.lastMessageAt && <span className="shrink-0 text-[11px] text-text-faint">{formatTimestamp(conversation.lastMessageAt)}</span>}
        </span>
        <span className="flex items-center justify-between gap-2">
          <span className="truncate text-xs text-text-muted">{conversation.lastMessagePreview ?? '—'}</span>
          {conversation.unreadCount > 0 && (
            <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-amber px-1.5 text-[11px] font-[650] text-night">{conversation.unreadCount}</span>
          )}
        </span>
      </span>
    </button>
  );
}

function ChatPane({ conversationId }: { conversationId: string | undefined }) {
  const { data } = useWhatsAppConversationQuery(conversationId);
  const sendMessage = useSendWhatsAppMessage();
  const toast = useToast();
  const [draft, setDraft] = useState('');

  if (!conversationId || !data) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center bg-ground text-sm text-text-muted">Select a conversation to view its messages.</div>
    );
  }

  const send = async () => {
    if (!draft.trim()) return;
    const text = draft.trim();
    setDraft('');
    try {
      await sendMessage.mutateAsync({ id: conversationId, input: { text } });
    } catch (err) {
      toast.error(err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to send message.');
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-ground">
      <div className="flex h-11 shrink-0 items-center border-b border-border bg-surface px-4">
        <span className="text-sm font-semibold text-ink">{data.conversation.profileName ?? data.conversation.phoneE164}</span>
        <span className="ml-2 text-xs text-text-muted">{data.conversation.phoneE164}</span>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-4">
        {data.messages.map((m) => <MessageBubble key={m.id} message={m} />)}
      </div>
      <div className="flex items-end gap-2 border-t border-border bg-surface p-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Type a message…"
          rows={1}
          className="min-h-9 flex-1 resize-none rounded-control border border-border-strong bg-surface px-3 py-2 text-sm"
        />
        <Button size="sm" onClick={send} loading={sendMessage.isPending}>
          Send
        </Button>
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: WhatsAppMessageResponse }) {
  const inbound = message.direction === 'inbound';
  const body = message.textBody ?? (message.mediaId ? `[${message.messageType}]` : message.templateName ? `[Template: ${message.templateName}]` : '[message]');
  return (
    <div className={`flex ${inbound ? 'justify-start' : 'justify-end'}`}>
      <div className={`max-w-[70%] rounded-[12px] px-3 py-2 text-[13px] leading-5 ${inbound ? 'bg-surface text-ink' : 'bg-primary text-white'}`}>
        <p className="whitespace-pre-wrap break-words">{body}</p>
        <p className={`mt-1 text-[10px] ${inbound ? 'text-text-faint' : 'text-white/70'}`}>
          {formatTimestamp(message.occurredAt)}
          {!inbound && message.status === 'failed' && ' · Failed'}
          {!inbound && message.status === 'read' && ' · Read'}
        </p>
      </div>
    </div>
  );
}

function InfoPane({ conversationId }: { conversationId: string | undefined }) {
  const { data } = useWhatsAppConversationQuery(conversationId);
  const { data: members } = useAssignableMembersQuery();
  const assign = useAssignWhatsAppConversation();

  if (!conversationId || !data) {
    return <aside className="flex min-h-0 flex-col border-l border-border bg-surface" />;
  }

  const { conversation } = data;

  return (
    <aside className="flex min-h-0 flex-col gap-4 overflow-y-auto border-l border-border bg-surface p-4">
      <div className="flex flex-col gap-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Contact</span>
        <span className="text-sm font-semibold text-ink">{conversation.profileName ?? '—'}</span>
        <span className="text-xs text-text-muted">{conversation.phoneE164}</span>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">CRM record</span>
        {conversation.linkedRecord ? (
          <Link
            to={conversation.linkedRecord.type === 'lead' ? '/leads/$leadId' : conversation.linkedRecord.type === 'contact' ? '/contacts/$contactId' : '/companies/$companyId'}
            params={conversation.linkedRecord.type === 'lead' ? { leadId: conversation.linkedRecord.id } : conversation.linkedRecord.type === 'contact' ? { contactId: conversation.linkedRecord.id } : { companyId: conversation.linkedRecord.id }}
            className="inline-flex w-fit items-center gap-1.5 rounded-[6px] border border-border bg-surface-subtle px-2 py-1 text-xs font-semibold text-primary hover:underline"
          >
            {conversation.linkedRecord.label}
          </Link>
        ) : (
          <span className="text-xs text-text-muted">Not linked to a CRM record yet.</span>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Assigned to</span>
        <select
          value={conversation.assignedMembershipId ?? ''}
          onChange={(e) => assign.mutate({ id: conversationId, input: { membershipId: e.target.value || null } })}
          className="h-9 rounded-control border border-border-strong bg-surface px-2.5 text-sm"
        >
          <option value="">Unassigned</option>
          {members?.map((m) => (
            <option key={m.id} value={m.id}>
              {m.displayName}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Status</span>
        <span className="text-xs text-text-secondary">{conversation.status === 'open' ? 'Open' : 'Closed'}</span>
      </div>
    </aside>
  );
}
